<script setup lang="ts">
import { OnboardingDialog, ToasterRoot } from '@proj-airi/stage-ui/components'
import { SERVER_URL } from '@proj-airi/stage-ui/libs/auth'
import { useSharedAnalyticsStore } from '@proj-airi/stage-ui/stores/analytics'
import { useAuthStore } from '@proj-airi/stage-ui/stores/auth'
import { useCharacterOrchestratorStore } from '@proj-airi/stage-ui/stores/character'
import { useChatSessionStore } from '@proj-airi/stage-ui/stores/chat/session-store'
import { useDisplayModelsStore } from '@proj-airi/stage-ui/stores/display-models'
import { useModsServerChannelStore } from '@proj-airi/stage-ui/stores/mods/api/channel-server'
import { useContextBridgeStore } from '@proj-airi/stage-ui/stores/mods/api/context-bridge'
import { useAiriCardStore } from '@proj-airi/stage-ui/stores/modules/airi-card'
import { useConsciousnessStore } from '@proj-airi/stage-ui/stores/modules/consciousness'
import { useSpeechStore } from '@proj-airi/stage-ui/stores/modules/speech'
import { useCommerceStore } from '@proj-airi/stage-ui/stores/commerce'
import { useOnboardingStore } from '@proj-airi/stage-ui/stores/onboarding'
import { useProvidersStore } from '@proj-airi/stage-ui/stores/providers'
import { useSettings } from '@proj-airi/stage-ui/stores/settings'
import { useTheme } from '@proj-airi/ui'
import { StageTransitionGroup } from '@proj-airi/ui-transitions'
import posthog from 'posthog-js'
import { storeToRefs } from 'pinia'
import { computed, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterView } from 'vue-router'
import { toast, Toaster } from 'vue-sonner'

import PerformanceOverlay from './components/Devtools/PerformanceOverlay.vue'

import { usePWAStore } from './stores/pwa'

usePWAStore()

const contextBridgeStore = useContextBridgeStore()
const authStore = useAuthStore()
const i18n = useI18n()
const displayModelsStore = useDisplayModelsStore()
const settingsStore = useSettings()
const settings = storeToRefs(settingsStore)
const { user, isAuthenticated } = storeToRefs(authStore)
const onboardingStore = useOnboardingStore()
const chatSessionStore = useChatSessionStore()
const serverChannelStore = useModsServerChannelStore()
const characterOrchestratorStore = useCharacterOrchestratorStore()
const providersStore = useProvidersStore()
const consciousnessStore = useConsciousnessStore()
const speechStore = useSpeechStore()
const commerceStore = useCommerceStore()
const { shouldShowSetup } = storeToRefs(onboardingStore)
const { isDark } = useTheme()
const cardStore = useAiriCardStore()
const analyticsStore = useSharedAnalyticsStore()

const isAdminPortalEnabled
  = import.meta.env.VITE_ADMIN_PORTAL_ENABLED === 'true'
    || import.meta.env.VITE_OPERATOR_MODE === 'true'

async function bootstrapConsumerProvider() {
  if (isAdminPortalEnabled)
    return

  const runtimeEnv = (import.meta.env.VITE_CONFIG_ENVIRONMENT || 'production').trim()
  let providerId = (import.meta.env.VITE_CONSUMER_CHAT_PROVIDER || 'openai-compatible').trim()
  let model = (import.meta.env.VITE_CONSUMER_CHAT_MODEL || 'gpt-4o-mini').trim()
  let apiKey = (import.meta.env.VITE_CONSUMER_CHAT_API_KEY || import.meta.env.VITE_OPENAI_API_KEY || '').trim()
  let baseUrl = (import.meta.env.VITE_CONSUMER_CHAT_BASE_URL || 'https://api.openai.com/v1').trim()
  let speechProviderId = (import.meta.env.VITE_CONSUMER_SPEECH_PROVIDER || 'openai-compatible-audio-speech').trim()
  let speechModel = (import.meta.env.VITE_CONSUMER_SPEECH_MODEL || 'gpt-4o-mini-tts').trim()
  let speechVoice = (import.meta.env.VITE_CONSUMER_SPEECH_VOICE || 'alloy').trim()
  let speechBaseUrl = (import.meta.env.VITE_CONSUMER_SPEECH_BASE_URL || baseUrl).trim()
  let proxyToken = ''
  commerceStore.setMarketingConfig()

  try {
    const response = await fetch(`${SERVER_URL}/api/runtime/public-configs/${runtimeEnv}?namespace=stage-web`, {
      credentials: 'include',
    })
    if (response.ok) {
      const runtime = await response.json() as {
        proxyToken?: string
        config?: {
          chat?: {
            providerId?: string
            model?: string
            baseUrl?: string
          }
          speech?: {
            providerId?: string
            model?: string
            voice?: string
            baseUrl?: string
          }
          marketing?: {
            promoEnabled?: boolean
            promoBadge?: string
            promoDescription?: string
            promoCta?: string
          }
        }
      }
      providerId = runtime.config?.chat?.providerId?.trim() || providerId
      model = runtime.config?.chat?.model?.trim() || model
      baseUrl = runtime.config?.chat?.baseUrl?.trim() || baseUrl
      speechProviderId = runtime.config?.speech?.providerId?.trim() || speechProviderId
      speechModel = runtime.config?.speech?.model?.trim() || speechModel
      speechVoice = runtime.config?.speech?.voice?.trim() || speechVoice
      speechBaseUrl = runtime.config?.speech?.baseUrl?.trim() || speechBaseUrl
      commerceStore.setMarketingConfig(runtime.config?.marketing)
      proxyToken = runtime.proxyToken?.trim() || ''
      apiKey = ''
    }
  }
  catch {
    // Fallback to local env defaults when runtime control plane is unavailable.
    commerceStore.setMarketingConfig()
  }

  providersStore.initializeProvider(providerId)
  const currentConfig = providersStore.getProviderConfig(providerId) || {}
  providersStore.providers[providerId] = {
    ...currentConfig,
    ...(baseUrl ? { baseUrl } : {}),
    ...((proxyToken || apiKey) ? { apiKey: proxyToken || apiKey } : {}),
  }

  if (apiKey)
    providersStore.forceProviderConfigured(providerId)

  try {
    await providersStore.fetchModelsForProvider(providerId)
  }
  catch {
    // Keep runtime resilient for consumer mode.
  }

  const hasActiveProvider = consciousnessStore.activeProvider
    && Object.prototype.hasOwnProperty.call(providersStore.providerMetadata, consciousnessStore.activeProvider)

  if (!hasActiveProvider)
    consciousnessStore.activeProvider = providerId
  if (!consciousnessStore.activeModel)
    consciousnessStore.activeModel = model

  providersStore.initializeProvider(speechProviderId)
  const currentSpeechConfig = providersStore.getProviderConfig(speechProviderId) || {}
  providersStore.providers[speechProviderId] = {
    ...currentSpeechConfig,
    ...(speechBaseUrl ? { baseUrl: speechBaseUrl } : {}),
    ...((proxyToken || apiKey) ? { apiKey: proxyToken || apiKey } : {}),
    ...(speechModel ? { model: speechModel } : {}),
    ...(speechVoice ? { voice: speechVoice } : {}),
  }

  if (speechStore.activeSpeechProvider !== speechProviderId)
    speechStore.activeSpeechProvider = speechProviderId
  if (!speechStore.activeSpeechModel)
    speechStore.activeSpeechModel = speechModel
  if (!speechStore.activeSpeechVoiceId)
    speechStore.activeSpeechVoiceId = speechVoice
}

const primaryColor = computed(() => {
  return isDark.value
    ? `color-mix(in srgb, oklch(95% var(--chromatic-chroma-900) calc(var(--chromatic-hue) + ${0})) 70%, oklch(50% 0 360))`
    : `color-mix(in srgb, oklch(95% var(--chromatic-chroma-900) calc(var(--chromatic-hue) + ${0})) 90%, oklch(90% 0 360))`
})

const secondaryColor = computed(() => {
  return isDark.value
    ? `color-mix(in srgb, oklch(95% var(--chromatic-chroma-900) calc(var(--chromatic-hue) + ${180})) 70%, oklch(50% 0 360))`
    : `color-mix(in srgb, oklch(95% var(--chromatic-chroma-900) calc(var(--chromatic-hue) + ${180})) 90%, oklch(90% 0 360))`
})

const tertiaryColor = computed(() => {
  return isDark.value
    ? `color-mix(in srgb, oklch(95% var(--chromatic-chroma-900) calc(var(--chromatic-hue) + ${60})) 70%, oklch(50% 0 360))`
    : `color-mix(in srgb, oklch(95% var(--chromatic-chroma-900) calc(var(--chromatic-hue) + ${60})) 90%, oklch(90% 0 360))`
})

const colors = computed(() => {
  return [primaryColor.value, secondaryColor.value, tertiaryColor.value, isDark.value ? '#121212' : '#FFFFFF']
})

watch(settings.language, () => {
  i18n.locale.value = settings.language.value
})

watch(settings.themeColorsHue, () => {
  document.documentElement.style.setProperty('--chromatic-hue', settings.themeColorsHue.value.toString())
}, { immediate: true })

watch(settings.themeColorsHueDynamic, () => {
  document.documentElement.classList.toggle('dynamic-hue', settings.themeColorsHueDynamic.value)
}, { immediate: true })

watch([user, isAuthenticated], ([nextUser, authed]) => {
  if (!authed || !nextUser?.id)
    return

  const loginUserId = nextUser.id
  const currentDistinctId = posthog.get_distinct_id()
  const aliasStorageKey = `mouly:posthog:aliased:${loginUserId}`

  if (currentDistinctId && currentDistinctId !== loginUserId && !globalThis.localStorage?.getItem(aliasStorageKey)) {
    posthog.alias(loginUserId, currentDistinctId)
    globalThis.localStorage?.setItem(aliasStorageKey, '1')
  }

  posthog.identify(loginUserId, {
    email: nextUser.email,
    name: nextUser.name,
    provider: nextUser.emailVerified ? 'verified' : 'unverified',
  })
}, { immediate: true })

// Initialize first-time setup check when app mounts
onMounted(async () => {
  analyticsStore.initialize()
  cardStore.initialize()
  await bootstrapConsumerProvider()

  onboardingStore.initializeSetupCheck()
  void displayModelsStore.loadDisplayModelsFromIndexedDB()
    .then(() => settingsStore.initializeStageModel())

  setTimeout(() => {
    void chatSessionStore.initialize()
    void serverChannelStore.initialize({ possibleEvents: ['ui:configure'] }).catch(err => console.error('Failed to initialize Mods Server Channel in App.vue:', err))
    void contextBridgeStore.initialize()
    characterOrchestratorStore.initialize()
  }, 0)
})

onUnmounted(() => {
  contextBridgeStore.dispose()
})

// Handle first-time setup events
function handleSetupConfigured() {
  onboardingStore.markSetupCompleted()
}

function handleSetupSkipped() {
  onboardingStore.markSetupSkipped()
}
</script>

<template>
  <StageTransitionGroup
    :primary-color="primaryColor"
    :secondary-color="secondaryColor"
    :tertiary-color="tertiaryColor"
    :colors="colors"
    :z-index="100"
    :disable-transitions="settings.disableTransitions.value"
    :use-page-specific-transitions="settings.usePageSpecificTransitions.value"
  >
    <RouterView v-slot="{ Component }">
      <KeepAlive :include="['IndexScenePage', 'StageScenePage']">
        <component :is="Component" />
      </KeepAlive>
    </RouterView>
  </StageTransitionGroup>

  <ToasterRoot @close="id => toast.dismiss(id)">
    <Toaster />
  </ToasterRoot>

  <!-- First Time Setup Dialog -->
  <OnboardingDialog
    v-model="shouldShowSetup"
    @configured="handleSetupConfigured"
    @skipped="handleSetupSkipped"
  />

  <PerformanceOverlay />
</template>

<style>
/* We need this to properly animate the CSS variable */
@property --chromatic-hue {
  syntax: '<number>';
  initial-value: 0;
  inherits: true;
}

@keyframes hue-anim {
  from {
    --chromatic-hue: 0;
  }
  to {
    --chromatic-hue: 360;
  }
}

.dynamic-hue {
  animation: hue-anim 10s linear infinite;
}
</style>
